import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '../lib/api';
import { usePageTitle } from './shellContext';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Hotel101 Inventory`;
  }, [title]);
  usePageTitle(title);
  return (
    <div className="pagehead">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, padded = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; padded?: boolean }) {
  return (
    <section className="card">
      {(title || actions) && (
        <div className="cardhead">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      {padded ? <div className="cardbody">{children}</div> : children}
    </section>
  );
}

export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && <div className="note">{note}</div>}
    </div>
  );
}

export function Badge({ tone, children }: { tone?: 'ok' | 'warn' | 'danger'; children: ReactNode }) {
  return <span className={`badge ${tone ?? ''}`}>{children}</span>;
}

const STATUS_TONES: Record<string, 'ok' | 'warn' | 'danger' | undefined> = {
  placed: 'warn', partially_received: 'warn', received: 'ok', cancelled: 'danger', draft: undefined, posted: 'ok', reversed: 'danger', active: 'ok', inactive: 'danger',
  submitted: 'warn', approved: 'ok', rejected: 'danger', converted: 'ok', partially_fulfilled: 'warn', fulfilled: 'ok', closed: undefined,
  attachment_generated: 'warn', signed: 'warn', filed: 'ok',
};
export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONES[status]}>{status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')}</Badge>;
}

/** Labelled form control. Pass the control as a child; the label is linked by id. */
export function Field({ label, hint, full, children, id }: { label: string; hint?: ReactNode; full?: boolean; children: (id: string) => ReactNode; id?: string }) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label htmlFor={fieldId}>{label}</label>
      {children(fieldId)}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" /> {label}
    </div>
  );
}

export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div className="notice error" role="alert">
      {errorMessage(error)} {onRetry && <button className="link" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

// Open dialogs, innermost last: only the top one reacts to Escape and traps Tab.
const openDialogs: HTMLElement[] = [];
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ title, onClose, children, footer, wide, dismissable = true }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; dismissable?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const dialog = ref.current!;
    const returnTo = document.activeElement as HTMLElement | null;
    openDialogs.push(dialog);
    if (!dialog.contains(document.activeElement)) dialog.focus();
    const onKey = (e: KeyboardEvent) => {
      if (openDialogs[openDialogs.length - 1] !== dialog) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (dismissable) close.current();
      } else if (e.key === 'Tab') {
        const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      openDialogs.splice(openDialogs.indexOf(dialog), 1);
      if (returnTo?.isConnected) returnTo.focus();
    };
  }, []);

  return (
    <div className="overlay" onMouseDown={e => dismissable && e.target === e.currentTarget && onClose()}>
      <div ref={ref} tabIndex={-1} className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="cardhead">
          <h2>{title}</h2>
          {dismissable && <button className="sm" onClick={onClose} aria-label="Close">✕</button>}
        </div>
        <div className="cardbody">{children}</div>
        {footer && <div className="footer">{footer}</div>}
      </div>
    </div>
  );
}

/** Asks for a required reason before an irreversible action such as a reversal. */
export function ReasonDialog({ title, message, confirmLabel, onConfirm, onClose }: {
  title: string; message: ReactNode; confirmLabel: string; onConfirm: (reason: string) => Promise<void>; onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose} footer={<>
      <button onClick={onClose}>Cancel</button>
      <button className="primary" disabled={busy || !reason.trim()} onClick={submit}>{busy ? 'Working…' : confirmLabel}</button>
    </>}>
      <ErrorNotice error={error} />
      <div className="notice">{message}</div>
      <Field label="Reason (required)">{id => <textarea id={id} value={reason} onChange={e => setReason(e.target.value)} maxLength={500} autoFocus />}</Field>
    </Modal>
  );
}

const ToastContext = createContext<(message: string, tone?: 'error') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<{ id: number; message: string; tone?: 'error' }[]>([]);
  const show = useCallback((message: string, tone?: 'error') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, tone }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), tone === 'error' ? 7000 : 4000);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map(t => <div key={t.id} className={`toast ${t.tone ?? ''}`}>{t.message}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/** Wraps a submit handler with busy/error state. */
export function useSubmit<A extends unknown[]>(fn: (...args: A) => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (...args: A) => {
    setBusy(true);
    setError(null);
    try {
      await fn(...args);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return { submit, busy, error, setError };
}
